import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose, MdSensors, MdDeveloperBoard, MdAdd } from 'react-icons/md';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useLanguage } from '../../context/LanguageContext';

const UNIT_SUGGESTIONS = [
    '°C', '°F', 'mS/cm', 'pH', '%', 'V', 'A', 'W', 'kW',
    'kPa', 'bar', 'psi', 'rpm', 'lux', 'ppm', 'mg/L', 'm³/h', 'L/min'
];

export default function AddSensorModal({ companyId, onClose, onSuccess }) {
    const { t } = useLanguage();
    const [systems, setSystems] = useState([]);
    const [loadingSystems, setLoadingSystems] = useState(true);
    const [selectedSystemId, setSelectedSystemId] = useState('');
    const [newSystemName, setNewSystemName] = useState('');
    const [creatingSystem, setCreatingSystem] = useState(false);
    const [showNewSystem, setShowNewSystem] = useState(false);

    const [sensorName, setSensorName] = useState('');
    const [unit, setUnit] = useState('');
    const [minThreshold, setMinThreshold] = useState('');
    const [maxThreshold, setMaxThreshold] = useState('');
    const [thresholdEditableByCompany, setThresholdEditableByCompany] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        const fetchSystems = async () => {
            try {
                setLoadingSystems(true);
                const res = await api.get(`/systems/company/${companyId}`);
                const list = res.data.data || [];
                setSystems(list);
                if (list.length === 0) {
                    setShowNewSystem(true);
                } else {
                    setSelectedSystemId(list[0]._id);
                }
            } catch {
                toast.error(t('addSensorModal.systemsLoadFailed'));
            } finally {
                setLoadingSystems(false);
            }
        };
        fetchSystems();
    }, [companyId, t]);

    const handleCreateSystem = async () => {
        if (!newSystemName.trim()) { toast.error(t('addSystemModal.systemRequired')); return; }
        try {
            setCreatingSystem(true);
            const res = await api.post('/systems', { companyId, systemName: newSystemName.trim() });
            const newSys = res.data.data;
            setSystems(prev => [...prev, newSys]);
            setSelectedSystemId(newSys._id);
            setNewSystemName('');
            setShowNewSystem(false);
            toast.success(t('addSensorModal.systemCreated'));
        } catch (err) {
            toast.error(err.response?.data?.message || t('addSensorModal.systemCreateFailed'));
        } finally {
            setCreatingSystem(false);
        }
    };

    const validate = () => {
        const errs = {};
        if (!selectedSystemId) errs.system = t('addSensorModal.systemRequired');
        if (!sensorName.trim()) errs.sensorName = t('addSensorModal.sensorRequired');
        if (!unit.trim()) errs.unit = t('addSensorModal.unitRequired');

        if (minThreshold !== '' && Number.isNaN(Number(minThreshold))) errs.threshold = t('addSensorModal.thresholdInvalidNumber');
        if (maxThreshold !== '' && Number.isNaN(Number(maxThreshold))) errs.threshold = t('addSensorModal.thresholdInvalidNumber');
        if (minThreshold !== '' && maxThreshold !== '' && Number(minThreshold) >= Number(maxThreshold)) {
            errs.threshold = t('addSensorModal.thresholdInvalidRange');
        }
        return errs;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length > 0) { setErrors(errs); return; }
        try {
            setLoading(true);
            await api.post('/sensors', {
                systemId: selectedSystemId,
                sensorName,
                unit,
                minThreshold: minThreshold === '' ? null : Number(minThreshold),
                maxThreshold: maxThreshold === '' ? null : Number(maxThreshold),
                thresholdEditableByCompany
            });
            toast.success(t('addSensorModal.sensorAdded'));
            onSuccess();
        } catch (err) {
            toast.error(err.response?.data?.message || t('addSensorModal.sensorAddFailed'));
        } finally {
            setLoading(false);
        }
    };

    const clearError = (field) => setErrors(prev => ({ ...prev, [field]: '' }));

    return (
        <AnimatePresence>
            <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={(e) => e.target === e.currentTarget && onClose()}>
                <motion.div className="modal" initial={{ opacity: 0, scale: 0.9, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.3 }}>

                    <div className="modal-header">
                        <div className="modal-title">
                            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(139,92,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--secondary)' }}>
                                <MdSensors />
                            </div>
                            {t('addSensorModal.title')}
                        </div>
                        <button className="modal-close" onClick={onClose} id="close-sensor-modal"><MdClose /></button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">

                            {/* ── Sistem Seçimi ── */}
                            <div className="form-group">
                                <label className="form-label">
                                    <MdDeveloperBoard style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                    {t('addSensorModal.systemLabel')}
                                </label>

                                {loadingSystems ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: 13, padding: '10px 0' }}>
                                        <span className="spinner spinner-sm" /> {t('common.loading')}
                                    </div>
                                ) : (
                                    <>
                                        {systems.length > 0 && !showNewSystem && (
                                            <div style={{ display: 'flex', gap: 8 }}>
                                                <select
                                                    className="form-select"
                                                    value={selectedSystemId}
                                                    onChange={(e) => { setSelectedSystemId(e.target.value); clearError('system'); }}
                                                    style={{ flex: 1 }}
                                                >
                                                    {systems.map(s => (
                                                        <option key={s._id} value={s._id}>{s.systemName}</option>
                                                    ))}
                                                </select>
                                                <button type="button" className="btn btn-secondary btn-sm btn-icon"
                                                    onClick={() => setShowNewSystem(true)} title={t('addSensorModal.newSystemCreate')} id="new-system-btn">
                                                    <MdAdd />
                                                </button>
                                            </div>
                                        )}

                                        {(showNewSystem || systems.length === 0) && (
                                            <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
                                                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                                                    {systems.length === 0 ? t('addSensorModal.noSystemsWarning') : `➕ ${t('addSensorModal.newSystemCreate')}`}
                                                </p>
                                                <div style={{ display: 'flex', gap: 8 }}>
                                                    <input className="form-input" placeholder={t('addSensorModal.systemNamePlaceholder')}
                                                        value={newSystemName} onChange={(e) => setNewSystemName(e.target.value)}
                                                        disabled={creatingSystem} id="inline-system-name"
                                                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleCreateSystem())} />
                                                    <button type="button" className="btn btn-success btn-sm" onClick={handleCreateSystem}
                                                        disabled={creatingSystem} id="create-system-btn">
                                                        {creatingSystem ? <span className="spinner spinner-sm" /> : t('common.create')}
                                                    </button>
                                                    {systems.length > 0 && (
                                                        <button type="button" className="btn btn-secondary btn-sm"
                                                            onClick={() => { setShowNewSystem(false); setNewSystemName(''); }}>
                                                            {t('common.cancel')}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                                {errors.system && <div className="form-error">{errors.system}</div>}
                            </div>

                            {/* ── Sensör Adı ── */}
                            <div className="form-group">
                                <label className="form-label" htmlFor="sensor-name">{t('addSensorModal.sensorName')}</label>
                                <input id="sensor-name" className="form-input"
                                    placeholder={t('addSensorModal.sensorNamePlaceholder')}
                                    value={sensorName} onChange={(e) => { setSensorName(e.target.value); clearError('sensorName'); }}
                                    disabled={loading} />
                                {errors.sensorName && <div className="form-error">{errors.sensorName}</div>}
                            </div>

                            {/* ── Birim ── */}
                            <div className="form-group">
                                <label className="form-label" htmlFor="sensor-unit">{t('addSensorModal.unitLabel')}</label>
                                <input id="sensor-unit" className="form-input"
                                    placeholder={t('addSensorModal.unitPlaceholder')}
                                    value={unit} onChange={(e) => { setUnit(e.target.value); clearError('unit'); }}
                                    disabled={loading} />
                                {errors.unit && <div className="form-error">{errors.unit}</div>}
                                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                                    {UNIT_SUGGESTIONS.map(u => (
                                        <button key={u} type="button"
                                            className="badge badge-primary"
                                            style={{ cursor: 'pointer', border: 'none', fontSize: 11, padding: '3px 9px', transition: 'all 0.15s' }}
                                            onClick={() => { setUnit(u); clearError('unit'); }}>
                                            {u}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* ── Eşik Değerleri (Opsiyonel) ── */}
                            <div className="form-group">
                                <label className="form-label">{t('addSensorModal.thresholdSectionTitle')}</label>
                                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -4, marginBottom: 10 }}>
                                    {t('addSensorModal.thresholdSectionDesc')}
                                </p>
                                <div style={{ display: 'flex', gap: 10 }}>
                                    <input
                                        type="number"
                                        className="form-input"
                                        placeholder={t('addSensorModal.minThresholdPlaceholder')}
                                        value={minThreshold}
                                        onChange={(e) => { setMinThreshold(e.target.value); clearError('threshold'); }}
                                        disabled={loading}
                                        id="min-threshold"
                                    />
                                    <input
                                        type="number"
                                        className="form-input"
                                        placeholder={t('addSensorModal.maxThresholdPlaceholder')}
                                        value={maxThreshold}
                                        onChange={(e) => { setMaxThreshold(e.target.value); clearError('threshold'); }}
                                        disabled={loading}
                                        id="max-threshold"
                                    />
                                </div>
                                {errors.threshold && <div className="form-error">{errors.threshold}</div>}

                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={thresholdEditableByCompany}
                                        onChange={(e) => setThresholdEditableByCompany(e.target.checked)}
                                        disabled={loading}
                                    />
                                    {t('addSensorModal.allowCompanyEdit')}
                                </label>
                            </div>

                            {/* ── Info Box ── */}
                            <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: 'var(--text-secondary)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                <span style={{ fontSize: 15 }}>💡</span>
                                <span>{t('addSensorModal.uuidAutoGenerated')}</span>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>{t('common.cancel')}</button>
                            <button type="submit" className="btn btn-primary" disabled={loading || !selectedSystemId} id="submit-sensor-btn">
                                {loading ? <><span className="spinner spinner-sm" /> {t('common.adding')}</> : <><MdSensors /> {t('addSensorModal.addSensor')}</>}
                            </button>
                        </div>
                    </form>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
